import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import SearchInput from "../../../components/Ui/Search";
import Pagination from "../../../components/Ui/Paging";
import { IconPlus } from "../../../components/Ui/Icon";
import {
  adminListCourses,
  adminCreateCourse,
  adminUpdateCourse,
  adminDeleteCourse,
  adminUpdateCourseStatus,
} from "../../../services/course";
import { getS3Presign } from "../../../store/upload";

// Data will be loaded from API; start with empty list

const CoursesList = () => {
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState({ page: 1, limit: 10, total: 0 });
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter((c) =>
      [c.title, c.description].some((t) => t?.toLowerCase().includes(s))
    );
  }, [data, search]);

  const paged = useMemo(() => {
    const start = (paging.page - 1) * paging.limit;
    const items = filtered.slice(start, start + paging.limit);
    return items;
  }, [filtered, paging.page, paging.limit]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / paging.limit));

  const handleCreate = async (course) => {
    setLoading(true);
    try {
      const created = await adminCreateCourse(course);
      setData((prev) => [created, ...prev]);
      setIsOpenCreate(false);
      toast.success("Created course successfully");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (course) => {
    setLoading(true);
    try {
      const updated = await adminUpdateCourse(course._id, course);
      setData((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
      setEditing(null);
      toast.success("Updated course");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update course");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await adminDeleteCourse(id);
      setData((prev) => prev.filter((c) => c._id !== id));
      toast.success("Deleted course");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete course");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (id) => {
    const current = data.find((d) => d._id === id);
    const next = !current.isActive;
    setLoading(true);
    try {
      const updated = await adminUpdateCourseStatus(id, next);
      setData((prev) => prev.map((c) => (c._id === id ? updated : c)));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await adminListCourses({ page: 1, limit: 100 });
        setData(res.data || []);
        setPaging((p) => ({ ...p, total: res.total || 0 }));
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load courses");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex w-full justify-between items-center p-3 flex-wrap md:flex-nowrap">
        <h2 className="font-semibold text-3xl text-left rtl:text-right text-gray-900">
          Courses
        </h2>
        <div className="inline-flex items-center gap-2">
          <a
            href="#create"
            onClick={(e) => setIsOpenCreate(true)}
            type="button"
            className="flex items-center p-2 text-sm font-medium h-full text-white bg-blue-700 rounded-lg border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300"
          >
            <IconPlus />
            <span> Create</span>
          </a>
          <SearchInput
            className="md:mr-28"
            onSubmitSearch={(val) => setSearch(val)}
          />
        </div>
      </div>

      <table className="w-full text-sm text-left rtl:text-right text-gray-500">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th className="px-6 py-3">Title</th>
            <th className="px-6 py-3">Extra</th>
            <th className="px-6 py-3">Active</th>
            <th className="px-6 py-3">Modules</th>
            <th className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {paged.map((c) => (
            <tr key={c._id} className="bg-white border-b">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <img
                    alt="thumb"
                    src={c.thumbnail}
                    className="w-16 h-10 object-cover rounded"
                  />
                  <div>
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-xs text-gray-400 line-clamp-1 max-w-[420px]">
                      {c.description}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">{String(c.isExtra)}</td>
              <td className="px-6 py-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={c.isActive}
                    onChange={() => toggleActive(c._id)}
                  />
                  <span className="relative w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:w-5 after:h-5 after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </td>
              <td className="px-6 py-4">{c.modules.length}</td>
              <td className="px-6 py-4">
                <div className="inline-flex gap-2">
                  <button
                    className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => setEditing(c)}
                  >
                    Edit
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded bg-rose-100 text-rose-700 hover:bg-rose-200"
                    onClick={() => handleDelete(c._id)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length > paging.limit && (
        <div className="p-3">
          <Pagination
            totalPages={totalPages}
            currentPage={paging.page}
            onPageChange={(p) => setPaging((s) => ({ ...s, page: p }))}
          />
        </div>
      )}

      {loading && <LoadingOverlay />}

      {(isOpenCreate || editing) && (
        <CourseForm
          initial={
            editing || {
              title: "",
              description: "",
              isExtra: false,
              isActive: true,
              thumbnail: "",
              durationDays: undefined,
              startDate: "",
              modules: undefined,
            }
          }
          onCancel={() => {
            setIsOpenCreate(false);
            setEditing(null);
          }}
          onSubmit={(values) => {
            if (editing) handleUpdate({ ...editing, ...values });
            else handleCreate(values);
          }}
        />
      )}
    </div>
  );
};

const CourseForm = ({ initial, onCancel, onSubmit }) => {
  const [values, setValues] = useState(initial);
  const [activeTab, setActiveTab] = useState("edit"); // edit | preview
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const computeAutoReleaseDates = (draft) => {
    if (!draft?.startDate) return draft;
    try {
      const base = new Date(draft.startDate);
      if (isNaN(base.getTime())) return draft;
      const newModules = (draft.modules || []).map((m) => ({
        ...m,
        lessons: (m.lessons || []).map((l) => {
          if (!l.releaseDate && l.dayNumber) {
            const d = new Date(base);
            d.setDate(base.getDate() + (Number(l.dayNumber) - 1));
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            return { ...l, releaseDate: `${yyyy}-${mm}-${dd}` };
          }
          return l;
        }),
      }));
      return { ...draft, modules: newModules };
    } catch {
      return draft;
    }
  };

  const validateCourse = (draft) => {
    const errs = [];
    const fieldErrs = {};
    if (!draft.title || !String(draft.title).trim()) fieldErrs.title = "Please enter a title";
    // Thumbnail validation
    const thumb = (draft.thumbnail || "").trim();
    const isValidThumb = /^https?:\/\//i.test(thumb);
    if (!thumb) {
      fieldErrs.thumbnail = "Thumbnail is required";
    } else if (!isValidThumb) {
      fieldErrs.thumbnail = "Thumbnail must be a valid URL";
    }
    // durationDays is now optional, but if provided must be >= 1
    if (draft.durationDays !== undefined && draft.durationDays !== null && draft.durationDays !== '') {
      const duration = Number(draft.durationDays);
      if (isNaN(duration) || duration < 1) {
        errs.push("Total Days must be >= 1 if provided");
        fieldErrs.durationDays = "Total days must be ≥ 1 if provided";
      }
    }
    // Validate lessons
    (draft.modules || []).forEach((m, mi) => {
      (m.lessons || []).forEach((l, li) => {
        if (!l.title || !String(l.title).trim()) {
          errs.push(`Module ${mi + 1} → Lesson ${li + 1}: title is required`);
        }
      });
    });
    (draft.modules || []).forEach((m, mi) => {
      (m.lessons || []).forEach((l, li) => {
        // dayNumber is now optional, but if provided must be valid
        if (l.dayNumber !== undefined && l.dayNumber !== null && l.dayNumber !== '') {
          const dayNum = Number(l.dayNumber);
          const courseDuration = draft.durationDays ? Number(draft.durationDays) : 0;
          if (isNaN(dayNum) || dayNum < 1 || (courseDuration > 0 && dayNum > courseDuration)) {
            errs.push(`Module ${mi + 1} → Lesson ${li + 1}: invalid Day`);
          }
        }
        if (!Array.isArray(l.resources) || l.resources.length < 1) {
          errs.push(`Module ${mi + 1} → Lesson ${li + 1}: add at least 1 resource`);
        }
      });
    });
    return { list: errs, fields: fieldErrs };
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      aria-modal
      role="dialog"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative bg-white w-full max-w-5xl rounded-xl shadow-2xl max-h-[85vh] flex flex-col border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 border-b bg-white flex items-center justify-between">
          <div className="font-semibold text-lg">{initial._id ? "Edit Course" : "Create Course"}</div>
          <button
            aria-label="Close"
            className="p-2 rounded hover:bg-gray-100"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          {/* Tabs */}
          <div className="flex gap-2 mb-2">
            <button
              className={`px-3 py-1.5 rounded-full border text-sm transition ${
                activeTab === "edit" ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("edit")}
            >
              Edit
            </button>
            <button
              className={`px-3 py-1.5 rounded-full border text-sm transition ${
                activeTab === "preview" ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("preview")}
            >
              Preview Days
            </button>
          </div>

          {!!errors.length && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700 text-sm">
              <div className="font-medium mb-1">Please fix the following:</div>
              <ul className="list-disc ms-5 space-y-0.5">
                {errors.map((er, i) => (
                  <li key={i}>{er}</li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === "edit" && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="grid grid-cols-1 gap-1">

            <div>
              <label className="text-sm">Title</label>
              <input
                value={values.title}
                onChange={(e) => {
                  setValues((s) => ({ ...s, title: e.target.value }));
                  if (fieldErrors.title) setFieldErrors((f) => ({ ...f, title: undefined }));
                }}
                className={`w-full mt-1 p-2 border rounded ${fieldErrors.title ? "border-rose-400" : ""}`}
                placeholder="Title"
              />
              {fieldErrors.title && (
                <div className="mt-1 text-xs text-rose-600">{fieldErrors.title}</div>
              )}
            </div>
            <div>
            <label className="text-sm">Description</label>
            <textarea
              value={values.description}
              onChange={(e) => setValues((s) => ({ ...s, description: e.target.value }))}
              className="w-full mt-1 p-2 border rounded"
              rows={3}
              placeholder="Short description"
            />
            </div>
            <div>
              <label className="text-sm">Total Days (Optional)</label>
              <input
                type="number"
                min={1}
                value={values.durationDays ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setValues((s) => {
                      const newValues = { ...s };
                      delete newValues.durationDays; // Remove the field entirely if empty
                      return newValues;
                    });
                  } else {
                    setValues((s) => ({ ...s, durationDays: Number(value) }));
                  }
                  if (fieldErrors.durationDays) setFieldErrors((f) => ({ ...f, durationDays: undefined }));
                }}
                className={`w-full mt-1 p-2 border rounded ${fieldErrors.durationDays ? "border-rose-400" : ""}`}
                placeholder="Leave empty for no day structure"
              />
              <div className="mt-1 text-xs text-gray-500">
                Leave empty to create a course without day-based structure
              </div>
              {fieldErrors.durationDays && (
                <div className="mt-1 text-xs text-rose-600">{fieldErrors.durationDays}</div>
              )}
            </div>
            </div>
            <div>
              <label className="text-sm">Thumbnail</label>
              <InlineThumbnailUpload
                value={values.thumbnail}
                onChange={(url) => {
                  setValues((s) => ({ ...s, thumbnail: url }));
                  if (fieldErrors.thumbnail) setFieldErrors((f) => ({ ...f, thumbnail: undefined }));
                }}
                error={fieldErrors.thumbnail}
              />
            </div>
          </div>
            

          <ModuleEditor values={values} setValues={setValues} />
          </>
          )}

          {activeTab === "preview" && (
            <PreviewDays values={values} />
          )}
        </div>
        <div className="sticky bottom-0 z-10 p-4 border-t bg-white flex justify-end gap-2">
          <button className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200" onClick={onCancel}>Cancel</button>
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => {
              const { list, fields } = validateCourse(values);
              setErrors(list);
              setFieldErrors(fields);
              if (list.length || Object.keys(fields).length) return;
              onSubmit(computeAutoReleaseDates(values));
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Inline uploader inside form with overlay preview and red remove button
const InlineThumbnailUpload = ({ value, onChange, error }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperRef, setCropperRef] = useState(null);
  const [cropperInstance, setCropperInstance] = useState(null);

  // Initialize cropper when modal opens
  useEffect(() => {
    if (showCropper && cropperRef && window.Cropper) {
      const cropper = new window.Cropper(cropperRef, {
        aspectRatio: 16 / 9, // You can adjust this ratio
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        zoomable: true,
        scalable: true,
        rotatable: true,
      });
      setCropperInstance(cropper);
      
      return () => {
        if (cropper) {
          cropper.destroy();
        }
      };
    }
  }, [showCropper, cropperRef]);

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setLocalError("");
    if (!file.type.startsWith("image/")) {
      setLocalError("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLocalError("Maximum size 5MB");
      return;
    }
    
    // Show cropper instead of direct upload
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropperImage(e.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedImage = async (croppedCanvas) => {
    try {
      setUploading(true);
      setShowCropper(false);
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        croppedCanvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      // Create a file from the blob
      const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      
      // Presign then direct PUT to S3
      const presign = await getS3Presign({
        filename: file.name,
        contentType: file.type,
        folderPath: "uploads/images",
      });
      if (!presign?.url) throw new Error("No presigned URL returned");
      const putRes = await fetch(presign.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error("S3 upload failed");
      const publicUrl = presign.publicUrl || "";
      if (publicUrl) onChange(publicUrl);
      else setLocalError("Upload failed, please try again");
    } catch (e) {
      setLocalError("Unable to upload");
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (e) => {
    const cb = e.clipboardData;
    if (!cb) return;
    const items = Array.from(cb.items || []);
    const imgItem = items.find((it) => it.type && it.type.startsWith("image/"));
    if (imgItem) {
      const file = imgItem.getAsFile();
      if (file && !uploading) {
        e.preventDefault();
        handleFiles([file]);
        return;
      }
    }
    const text = cb.getData && cb.getData("text");
    if (text && /^https?:\/\//i.test(text)) {
      // validate URL by preloading image
      const img = new Image();
      img.onload = () => onChange(text.trim());
      img.onerror = () => setLocalError("Invalid image URL");
      img.src = text.trim();
    }
  };

  return (
    <div
      className={`relative w-full rounded-lg border-2 border-dashed transition ${
        dragOver ? "border-blue-400 bg-blue-50" : error ? "border-rose-400 bg-rose-50/30" : "border-gray-200 bg-gray-50"
      }`}
      tabIndex={0}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onPaste={onPaste}
    >
      {/* Preview overlay */}
      {value && (
        <div className="absolute inset-0 group">
          <img src={value} alt="thumbnail" className="w-full h-full object-cover rounded-md" />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-md" />
          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              type="button"
              aria-label="Edit image"
              className="w-7 h-7 rounded-full bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 flex items-center justify-center shadow-sm"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => handleFiles(e.target.files);
                input.click();
              }}
            >
              ✏️
            </button>
            <button
              type="button"
              aria-label="Remove image"
              className="w-7 h-7 rounded-full bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 shadow-sm"
              onClick={() => onChange("")}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className={`flex flex-col items-center justify-center text-center gap-2 py-10 ${value ? "opacity-0 pointer-events-none" : ""}`}>
        <div className="text-3xl">☁️</div>
        <div className="font-medium">Drag & drop, click to choose, or paste (Cmd/Ctrl+V)</div>
        <div className="text-xs text-gray-400">PNG, JPG, GIF (max 5MB)</div>
        <label className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded bg-white border hover:bg-gray-50 cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          <span>Choose image</span>
        </label>
        {uploading && <div className="text-xs text-gray-500">Uploading...</div>}
        {(error || localError) && <div className="text-xs text-rose-600">{error || localError}</div>}
      </div>
      {(error || localError) && !value && (
        <div className="absolute -bottom-5 left-0 text-xs text-rose-600">{error || localError}</div>
      )}
      
      {/* Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Crop & Edit Image</h3>
              <button
                onClick={() => setShowCropper(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <img
                ref={setCropperRef}
                src={cropperImage}
                alt="Crop me"
                style={{ maxWidth: '100%', maxHeight: '400px' }}
                className="mx-auto"
              />
            </div>
            
            <div className="mb-4 flex gap-2 justify-center">
              <button
                onClick={() => cropperInstance?.rotate(-90)}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                ↺ Rotate Left
              </button>
              <button
                onClick={() => cropperInstance?.rotate(90)}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                ↻ Rotate Right
              </button>
              <button
                onClick={() => cropperInstance?.scaleX(-1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                ↔ Flip Horizontal
              </button>
              <button
                onClick={() => cropperInstance?.scaleY(-1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                ↕ Flip Vertical
              </button>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCropper(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (cropperInstance) {
                    const canvas = cropperInstance.getCroppedCanvas({
                      maxWidth: 1200,
                      maxHeight: 1200,
                      imageSmoothingEnabled: true,
                      imageSmoothingQuality: 'high',
                    });
                    handleCroppedImage(canvas);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ModuleEditor = ({ values, setValues }) => {
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [openModules, setOpenModules] = useState({});

  const addModule = () => {
    if (!newModuleTitle.trim()) return;
    const nextOrder = (values.modules?.length || 0) + 1;
    const mod = {
      _id: `module_${Date.now()}`,
      title: newModuleTitle,
      order: nextOrder,
      lessons: [],
    };
    setValues((s) => ({ ...s, modules: [...(s.modules || []), mod] }));
    setNewModuleTitle("");
  };

  const removeModule = (id) => {
    setValues((s) => ({ ...s, modules: (s.modules || []).filter((m) => m._id !== id) }));
  };

  const moveModule = (id, dir) => {
    const mods = [...(values.modules || [])];
    const idx = mods.findIndex((m) => m._id === id);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= mods.length) return;
    [mods[idx], mods[swapIdx]] = [mods[swapIdx], mods[idx]];
    mods.forEach((m, i) => (m.order = i + 1));
    setValues((s) => ({ ...s, modules: mods }));
  };

  const toggleOpen = (id) => {
    setOpenModules((s) => ({ ...s, [id]: !s[id] }));
  };

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="font-semibold mb-2 text-lg">Modules</div>
      <div className="flex gap-2 mb-3">
        <input
          className="flex-1 p-2 border rounded"
          placeholder="New module title"
          value={newModuleTitle}
          onChange={(e) => setNewModuleTitle(e.target.value)}
        />
        <button className="px-3 py-2 rounded cursor-pointer bg-gray-800 text-white hover:bg-gray-700" onClick={addModule}>
          Add Module
        </button>
      </div>
      <div className="space-y-3">
        {(values.modules || []).map((m) => (
          <div key={m._id} className="border rounded-lg p-2 shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold bg-gray-200 rounded-full">
                  {m.order}
                </span>
                <div className="font-medium text-gray-800">{m.title}</div>
              </div>
              <div className="inline-flex gap-2">
                <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => moveModule(m._id, "up")}>Up</button>
                <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => moveModule(m._id, "down")}>Down</button>
                <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => toggleOpen(m._id)}>
                  {openModules[m._id] ? "Collapse" : "Expand"}
                </button>
                <button className="px-2 py-1 text-xs bg-rose-100 text-rose-700 rounded hover:bg-rose-200" onClick={() => removeModule(m._id)}>Remove</button>
              </div>
            </div>

            {openModules[m._id] !== false && (
              <LessonEditor moduleItem={m} values={values} setValues={setValues} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const LessonEditor = ({ moduleItem, values, setValues }) => {
  const [draft, setDraft] = useState({
    title: "",
    subtitle: "",
    description: "",
    resources: [],
    dayNumber: 1,
    releaseDate: "",
  });
  const [resourceDraft, setResourceDraft] = useState({ type: "video", url: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingRes, setUploadingRes] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const isYouTubeUrl = (url) => {
    return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//i.test(url || "");
  };

  const addLesson = () => {
    const errs = {};
    if (!draft.title.trim()) errs.title = "Please enter a lesson title";
    if (!Array.isArray(draft.resources) || draft.resources.length < 1)
      errs.resources = "Add at least 1 resource";
    const duration = values.durationDays ? Number(values.durationDays) : 0;
    // dayNumber is now optional, but if provided must be valid
    if (draft.dayNumber !== undefined && draft.dayNumber !== null && draft.dayNumber !== '') {
      const dayNum = Number(draft.dayNumber);
      if (isNaN(dayNum) || dayNum < 1 || (duration > 0 && dayNum > duration)) {
        errs.dayNumber = "Invalid day for this course";
      }
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    const nextOrder = (moduleItem.lessons?.length || 0) + 1;
    const ls = {
      _id: `lesson_${Date.now()}`,
      order: nextOrder,
      title: draft.title,
      subtitle: draft.subtitle,
      description: draft.description,
      resources: draft.resources || [],
      dayNumber: draft.dayNumber,
      releaseDate: draft.releaseDate || "",
    };
    setValues((s) => ({
      ...s,
      modules: (s.modules || []).map((m) =>
        m._id === moduleItem._id ? { ...m, lessons: [...(m.lessons || []), ls] } : m
      ),
    }));
    setDraft({ title: "", subtitle: "", description: "", resources: [], dayNumber: 1, releaseDate: "" });
    setResourceDraft({ type: "video", url: "" });
    setFieldErrors({});
  };

  const removeLesson = (lessonId) => {
    setValues((s) => ({
      ...s,
      modules: (s.modules || []).map((m) =>
        m._id === moduleItem._id
          ? { ...m, lessons: (m.lessons || []).filter((l) => l._id !== lessonId) }
          : m
      ),
    }));
  };

  const moveLesson = (lessonId, dir) => {
    const lessons = [...(moduleItem.lessons || [])];
    const idx = lessons.findIndex((l) => l._id === lessonId);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= lessons.length) return;
    [lessons[idx], lessons[swapIdx]] = [lessons[swapIdx], lessons[idx]];
    lessons.forEach((l, i) => (l.order = i + 1));
    setValues((s) => ({
      ...s,
      modules: (s.modules || []).map((m) =>
        m._id === moduleItem._id ? { ...m, lessons } : m
      ),
    }));
  };

  const addResource = () => {
    const url = resourceDraft.url.trim();
    if (!url) {
      setFieldErrors((f) => ({ ...f, resourceUrl: "Please enter a URL or embed code" }));
      return;
    }
    const looksLikeUrl = /^(https?:)?\/\//i.test(url);
    if (!looksLikeUrl && resourceDraft.type !== "video") {
      setFieldErrors((f) => ({ ...f, resourceUrl: "Please provide a valid URL" }));
      return;
    }
    setDraft((s) => ({ ...s, resources: [...(s.resources || []), resourceDraft] }));
    setResourceDraft({ type: "video", url: "" });
    setFieldErrors((f) => ({ ...f, resourceUrl: undefined }));
  };

  const handleUploadResource = async (file) => {
    if (!file) return;
    try {
      setUploadingRes(true);
      setUploadPct(0);
      const type = file.type.startsWith("video/") ? "video" : file.type === "application/pdf" ? "pdf" : null;
      if (!type) {
        setFieldErrors((f) => ({ ...f, resourceUrl: "Only PDF or Video files are allowed" }));
        return;
      }
      const presign = await getS3Presign({ filename: file.name, contentType: file.type, folderPath: "uploads/course-resources" });
      if (!presign?.url) throw new Error("No presigned URL");
      
      // Use XHR to get progress events
      const xhr = new XMLHttpRequest();
      const putPromise = new Promise((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadPct(pct);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(true);
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload network error"));
        xhr.open("PUT", presign.url);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.send(file);
      });
      await putPromise;
      setResourceDraft({ type, url: presign.publicUrl });
      setFieldErrors((f) => ({ ...f, resourceUrl: undefined }));
    } catch (e) {
      setFieldErrors((f) => ({ ...f, resourceUrl: "Failed to upload to S3" }));
    } finally {
      setUploadingRes(false);
      setTimeout(() => setUploadPct(0), 500);
    }
  };

  const removeResource = (idx) => {
    setDraft((s) => ({
      ...s,
      resources: (s.resources || []).filter((_, i) => i !== idx),
    }));
  };

  const totalDays = values.durationDays ? Number(values.durationDays) : 7;

  return (
    <div className="mt-2 border-t pt-2">
      <div className="font-semibold mb-2 text-gray-800">Lessons</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 bg-white border rounded-lg p-3">
        <input
          className={`p-2 border rounded ${fieldErrors.title ? "border-rose-400" : ""}`}
          placeholder="Title"
          value={draft.title}
          onChange={(e) => {
            setDraft((s) => ({ ...s, title: e.target.value }));
            if (fieldErrors.title) setFieldErrors((f) => ({ ...f, title: undefined }));
          }}
        />
        {fieldErrors.title && (
          <div className="text-xs text-rose-600 -mt-2">{fieldErrors.title}</div>
        )}
        <input
          className="p-2 border rounded"
          placeholder="Subtitle"
          value={draft.subtitle}
          onChange={(e) => setDraft((s) => ({ ...s, subtitle: e.target.value }))}
        />
        <input
          className="p-2 border rounded"
          placeholder="Description"
          value={draft.description}
          onChange={(e) => setDraft((s) => ({ ...s, description: e.target.value }))}
        />
        <div>
          <label className="text-sm">Day (Optional)</label>
          <select
            className={`w-full mt-1 p-2 border rounded ${fieldErrors.dayNumber ? "border-rose-400" : ""}`}
            value={draft.dayNumber || ""}
            onChange={(e) => {
              const value = e.target.value;
              setDraft((s) => ({ ...s, dayNumber: value ? Number(value) : undefined }));
              if (fieldErrors.dayNumber) setFieldErrors((f) => ({ ...f, dayNumber: undefined }));
            }}
          >
            <option value="">No day assigned</option>
            {Array.from({ length: totalDays }).map((_, i) => (
              <option key={i + 1} value={i + 1}>{`Day ${i + 1}`}</option>
            ))}
          </select>
          <div className="mt-1 text-xs text-gray-500">
            Leave unselected if course has no day structure
          </div>
          {fieldErrors.dayNumber && (
            <div className="mt-1 text-xs text-rose-600">{fieldErrors.dayNumber}</div>
          )}
        </div>
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-start">
          <select
            className="p-2 border rounded"
            value={resourceDraft.type}
            onChange={(e) => setResourceDraft((s) => ({ ...s, type: e.target.value }))}
          >
            <option value="video">Video (YouTube/S3)</option>
            <option value="pdf">PDF</option>
          </select>
          <input
            className={`p-2 border rounded md:col-span-3 ${fieldErrors.resourceUrl ? "border-rose-400" : ""}`}
            placeholder={resourceDraft.type === "video" ? "YouTube link or S3 video URL" : "PDF file URL"}
            value={resourceDraft.url}
            onChange={(e) => {
              setResourceDraft((s) => ({ ...s, url: e.target.value }));
              if (fieldErrors.resourceUrl) setFieldErrors((f) => ({ ...f, resourceUrl: undefined }));
            }}
          />
          <button className="px-3 py-2 rounded bg-gray-800 text-white hover:bg-gray-700" onClick={addResource}>
            Add Resource
          </button>
          <label className="inline-flex items-center justify-center px-3 py-2 rounded border bg-white hover:bg-gray-50 cursor-pointer">
            <input
              type="file"
              accept="video/*,application/pdf"
              className="hidden"
              onChange={(e) => handleUploadResource(e.target.files?.[0])}
            />
            {uploadingRes ? `Uploading ${uploadPct}%` : "Upload to S3"}
          </label>
          {uploadingRes && (
            <div className="md:col-span-6 mt-2">
              <div className="w-full h-2 bg-gray-200 rounded">
                <div
                  className="h-2 bg-blue-600 rounded transition-all"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            </div>
          )}
          {fieldErrors.resourceUrl && (
            <div className="text-xs text-rose-600 md:col-span-6">{fieldErrors.resourceUrl}</div>
          )}
        </div>
        {!!(draft.resources || []).length && (
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(draft.resources || []).map((r, i) => (
              <div key={i} className="border rounded p-2 bg-gray-50 relative">
                <button
                  className="absolute top-1 right-1 text-rose-600 text-xs bg-white rounded-full w-6 h-6"
                  onClick={() => removeResource(i)}
                  title="Remove"
                >
                  ×
                </button>
                {r.type === "video" ? (
                  isYouTubeUrl(r.url) ? (
                    <div className="aspect-video w-full">
                      <iframe
                        title={`video-${i}`}
                        className="w-full h-full rounded"
                        src={r.url.replace("watch?v=", "embed/")}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <video src={r.url} controls className="w-full rounded" />
                  )
                ) : (
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-700">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-amber-100 text-amber-700">PDF</span>
                    <span className="truncate" title={r.url}>{r.url}</span>
                  </div>
                )}
                <div className="mt-2 text-[10px] uppercase tracking-wide text-gray-500">{r.type}</div>
              </div>
            ))}
          </div>
        )}
        {fieldErrors.resources && (
          <div className="md:col-span-2 text-xs text-rose-600">{fieldErrors.resources}</div>
        )}
      </div>
      <button
        className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        onClick={addLesson}
      >
        Add Lesson
      </button>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {[...(moduleItem.lessons || [])].sort((a, b) => (a.dayNumber || 0) - (b.dayNumber || 0) || (a.order || 0) - (b.order || 0)).map((l) => (
          <div
            key={l._id}
            className={`border rounded-lg p-3 shadow-sm hover:shadow transition bg-white`}
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold bg-gray-200 rounded-full">
                {l.order}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-medium truncate">{l.title}</div>
                  <div className="flex flex-wrap gap-1">
                    {l.dayNumber && (
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-700">{`Day ${l.dayNumber}`}</span>
                    )}
                    {(l.resources || []).map((r, i) => (
                      <span
                        key={i}
                        className={`px-2 py-0.5 text-[10px] rounded-full ${
                          r.type === "video"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {r.type.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
                {l.subtitle && (
                  <div className="text-xs text-gray-500 truncate">{l.subtitle}</div>
                )}
                {l.description && (
                  <div className="text-xs text-gray-400 line-clamp-2">{l.description}</div>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => moveLesson(l._id, "up")}>Up</button>
              <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => moveLesson(l._id, "down")}>Down</button>
              <button className="px-2 py-1 text-xs bg-rose-100 text-rose-700 rounded hover:bg-rose-200" onClick={() => removeLesson(l._id)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoursesList;

const PreviewDays = ({ values }) => {
  const duration = Number(values.durationDays || 0);
  const lessons = (values.modules || []).flatMap((m) => m.lessons || []);
  
  // If no duration is set, show modules directly
  if (!duration) {
    return (
      <div className="space-y-3">
        {(values.modules || []).map((module, moduleIndex) => (
          <div key={module._id || moduleIndex} className="border rounded-lg p-3 bg-white">
            <div className="font-semibold mb-2">{module.title}</div>
            {module.lessons && module.lessons.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {module.lessons.map((l) => (
                  <div key={l._id} className="border rounded p-3">
                    <div className="font-medium">{l.title}</div>
                    {l.subtitle && (
                      <div className="text-xs text-gray-500">{l.subtitle}</div>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(l.resources || []).map((r, i) => (
                        <span
                          key={i}
                          className={`px-2 py-0.5 text-[10px] rounded-full ${
                            r.type === "video"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {r.type.toUpperCase()}
                        </span>
                      ))}
                    </div>
                    {l.releaseDate && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        Release: {l.releaseDate}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400">No lessons</div>
            )}
          </div>
        ))}
      </div>
    );
  }
  
  // Original day-based structure
  const groups = new Map();
  lessons.forEach((l) => {
    const d = Number(l.dayNumber || 1);
    const arr = groups.get(d) || [];
    arr.push(l);
    groups.set(d, arr);
  });
  const orderedDays = Array.from({ length: duration }).map((_, i) => i + 1);
  return (
    <div className="space-y-3">
      {orderedDays.map((day) => {
        const ls = (groups.get(day) || []).sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );
        return (
          <div key={day} className="border rounded-lg p-3 bg-white">
            <div className="font-semibold mb-2">{`Day ${day}`}</div>
            {ls.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ls.map((l) => (
                  <div key={l._id} className="border rounded p-3">
                    <div className="font-medium">{l.title}</div>
                    {l.subtitle && (
                      <div className="text-xs text-gray-500">{l.subtitle}</div>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(l.resources || []).map((r, i) => (
                        <span
                          key={i}
                          className={`px-2 py-0.5 text-[10px] rounded-full ${
                            r.type === "video"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {r.type.toUpperCase()}
                        </span>
                      ))}
                    </div>
                    {l.releaseDate && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        Release: {l.releaseDate}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400">No lessons</div>
            )}
          </div>
        );
      })}
    </div>
  );
};


